import React, { useState, useEffect } from 'react';
import { 
  Folder, 
  File, 
  Image, 
  Video, 
  Music, 
  FileText, 
  Download, 
  Eye, 
  Play, 
  Trash2, 
  Copy, 
  Search, 
  Filter, 
  Upload, 
  Cloud, 
  Database,
  MoreVertical,
  CheckSquare,
  Square,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Calendar,
  HardDrive
} from 'lucide-react';
import { adminAPI } from '../../services/api';

const FileManagement = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStorage, setFilterStorage] = useState('');
  const [filterType, setFilterType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    file: null,
    folder: 'uploads',
    fileType: 'document'
  });
  const [message, setMessage] = useState('');
  const [messageStatus, setMessageStatus] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  useEffect(() => {
    fetchFiles();
  }, [currentPage, searchTerm, filterStorage, filterType]);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: 20,
        search: searchTerm,
        storageType: filterStorage,
        fileType: filterType
      };

      const response = await adminAPI.getS3Files(params);
      if (response.data.success) {
        setFiles(response.data.data.files);
        setTotalPages(response.data.data.pagination.pages);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      showMessage('Failed to fetch files', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg, status = 'success') => {
    setMessage(msg);
    setMessageStatus(status);
    setTimeout(() => {
      setMessage('');
      setMessageStatus('');
    }, 3000);
  };

  const handleFileSelect = (fileId) => {
    setSelectedFiles(prev => 
      prev.includes(fileId) 
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const handleSelectAll = () => {
    if (selectedFiles.length === files.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(files.map(file => file.id));
    }
  };

  const handleDeleteFile = async (fileId) => {
    setFileToDelete(fileId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteFile = async () => {
    if (!fileToDelete) return;

    try {
      const response = await adminAPI.deleteS3File(fileToDelete);
      if (response.data.success) {
        showMessage('File deleted successfully');
        fetchFiles();
        setSelectedFiles(prev => prev.filter(id => id !== fileToDelete));
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      showMessage('Failed to delete file', 'error');
    } finally {
      setFileToDelete(null);
      setShowDeleteConfirm(false);
    }
  };

  const handleBulkDelete = () => {
    if (selectedFiles.length === 0) {
      showMessage('No files selected', 'error');
      return;
    }
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    try {
      const response = await adminAPI.bulkDeleteS3Files(selectedFiles);
      if (response.data.success) {
        showMessage(`${response.data.data.deletedCount} files deleted successfully`);
        fetchFiles();
        setSelectedFiles([]);
      }
    } catch (error) {
      console.error('Error bulk deleting files:', error);
      showMessage('Failed to delete files', 'error');
    } finally {
      setShowBulkDeleteConfirm(false);
    }
  };

  const handleCopyLink = async (file) => {
    try {
      await navigator.clipboard.writeText(file.url);
      showMessage('Link copied to clipboard');
    } catch (error) {
      console.error('Error copying link:', error);
      showMessage('Failed to copy link', 'error');
    }
  };

  const handlePreview = (file) => {
    setPreviewFile(file);
    setShowPreviewModal(true);
  };

  const handleUpload = async () => {
    if (!uploadForm.file) {
      showMessage('Please select a file', 'error');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('folder', uploadForm.folder);
      formData.append('fileType', uploadForm.fileType);

      const response = await adminAPI.uploadS3File(formData);
      if (response.data.success) {
        showMessage('File uploaded successfully');
        setShowUploadModal(false);
        setUploadForm({ file: null, folder: 'uploads', fileType: 'document' });
        fetchFiles();
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      showMessage('Failed to upload file', 'error');
    } finally {
      setUploading(false);
    }
  };

  const getFileIcon = (file) => {
    if (file.isImage) return Image;
    if (file.isVideo) return Video;
    if (file.isAudio) return Music;
    if (file.isDocument) return FileText;
    return File;
  };

  const getStorageIcon = (storageType) => {
    return storageType === 's3' ? Cloud : Database;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const FileCard = ({ file }) => {
    const FileIcon = getFileIcon(file);
    const StorageIcon = getStorageIcon(file.storage_type);
    const isSelected = selectedFiles.includes(file.id);

    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg border-2 transition-all ${
        isSelected ? 'border-blue-500 shadow-lg' : 'border-gray-200 dark:border-gray-700'
      }`}>
        <div className="p-4">
          {/* File Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleFileSelect(file.id)}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <FileIcon className="h-8 w-8 text-gray-500" />
            </div>
            <div className="flex items-center space-x-1">
              <span className={`px-2 py-1 text-xs font-medium rounded-full flex items-center ${
                file.storage_type === 's3' 
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
              }`}>
                <StorageIcon className="h-3 w-3 mr-1" />
                {file.storage_type.toUpperCase()}
              </span>
            </div>
          </div>

          {/* File Info */}
          <div className="mb-3">
            <h3 className="font-medium text-gray-900 dark:text-white text-sm truncate" title={file.original_name}>
              {file.original_name}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {formatFileSize(file.file_size)} • {file.file_type}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {formatDate(file.created_at)}
            </p>
          </div>

          {/* File Preview (for images) */}
          {file.isImage && (
            <div className="mb-3">
              <img 
                src={file.url} 
                alt={file.original_name}
                className="w-full h-24 object-cover rounded border border-gray-200 dark:border-gray-600"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <button
                onClick={() => handlePreview(file)}
                className="p-1 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400"
                title="Preview"
              >
                <Eye className="h-4 w-4" />
              </button>
              
              <a
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1 text-gray-500 hover:text-green-600 dark:hover:text-green-400"
                title="Open in new tab"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
              
              <button
                onClick={() => handleCopyLink(file)}
                className="p-1 text-gray-500 hover:text-purple-600 dark:hover:text-purple-400"
                title="Copy link"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
            
            <div className="flex items-center space-x-1">
              <a
                href={file.url}
                download={file.original_name}
                className="p-1 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400"
                title="Download"
              >
                <Download className="h-4 w-4" />
              </a>
              
              <button
                onClick={() => handleDeleteFile(file.id)}
                className="p-1 text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <Folder className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
          File Management
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Manage all uploaded files from both local storage and AWS S3
        </p>
      </div>

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search files..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            
            <select
              value={filterStorage}
              onChange={(e) => setFilterStorage(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="">All Storage</option>
              <option value="local">Local Storage</option>
              <option value="s3">AWS S3</option>
            </select>
            
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="">All Types</option>
              <option value="image">Images</option>
              <option value="video">Videos</option>
              <option value="audio">Audio</option>
              <option value="document">Documents</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            {selectedFiles.length > 0 && (
              <button
                onClick={handleBulkDelete}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete ({selectedFiles.length})
              </button>
            )}
            
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload File
            </button>
            
            <button
              onClick={fetchFiles}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Select All */}
        {files.length > 0 && (
          <div className="mt-4 flex items-center space-x-2">
            <input
              type="checkbox"
              checked={selectedFiles.length === files.length}
              onChange={handleSelectAll}
              className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Select All ({selectedFiles.length}/{files.length})
            </span>
          </div>
        )}
      </div>

      {/* Files Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : files.length === 0 ? (
        <div className="text-center py-12">
          <Folder className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No files found</h3>
          <p className="text-gray-500 dark:text-gray-400">Upload your first file to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:p-6">
          {files.map(file => (
            <FileCard key={file.id} file={file} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50"
          >
            Previous
          </button>
          
          <span className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Upload File</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  File
                </label>
                <input
                  type="file"
                  onChange={(e) => setUploadForm(prev => ({ ...prev, file: e.target.files[0] }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Folder
                </label>
                <input
                  type="text"
                  value={uploadForm.folder}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, folder: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  File Type
                </label>
                <select
                  value={uploadForm.fileType}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, fileType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="document">Document</option>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                  <option value="audio">Audio</option>
                </select>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || !uploadForm.file}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewFile && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 max-w-4xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {previewFile.original_name}
              </h3>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              {previewFile.isImage ? (
                <img 
                  src={previewFile.url} 
                  alt={previewFile.original_name}
                  className="max-w-full h-auto rounded"
                />
              ) : previewFile.isVideo ? (
                <video 
                  src={previewFile.url} 
                  controls 
                  className="max-w-full h-auto rounded"
                />
              ) : previewFile.isAudio ? (
                <audio 
                  src={previewFile.url} 
                  controls 
                  className="w-full"
                />
              ) : (
                <div className="text-center py-8">
                  <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">
                    Preview not available for this file type
                  </p>
                  <a
                    href={previewFile.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center mt-2 text-blue-600 hover:text-blue-700"
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Open in new tab
                  </a>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Size:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">
                    {formatFileSize(previewFile.file_size)}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Type:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">
                    {previewFile.file_type}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Storage:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">
                    {previewFile.storage_type.toUpperCase()}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Uploaded:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">
                    {formatDate(previewFile.created_at)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 text-red-500 mr-2 sm:mr-3" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete File</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete this file? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setFileToDelete(null);
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteFile}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 text-red-500 mr-2 sm:mr-3" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Files</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete {selectedFiles.length} files? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowBulkDeleteConfirm(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete {selectedFiles.length} Files
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message */}
      {message && (
        <div className={`fixed bottom-4 right-4 p-4 rounded-lg flex items-center ${
          messageStatus === 'success' 
            ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200' 
            : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
        }`}>
          {messageStatus === 'success' ? (
            <CheckCircle className="h-5 w-5 mr-2" />
          ) : (
            <AlertTriangle className="h-5 w-5 mr-2" />
          )}
          {message}
        </div>
      )}
    </div>
  );
};

export default FileManagement;
